import { InjectionToken } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

/**
 * Query payload for credit cards table requests.
 */
export interface CreditCardsTableQuery {
  cnpj: string;
  page: number;
  offset: number;
  search?: string;
}

/**
 * Row model consumed by the credit cards table.
 */
export interface CreditCardsTableItem {
  id?: string;
  bank: string;
  bankCode?: string;
  institutionCnpj?: string;
  brand: string;
  name?: string;
  contractedProduct: string;
  totalLimit: number | null;
  usedLimit: number | null;
  availableLimit: number | null;
  isLimitFlexible?: boolean;
  dueDay?: number;
}

/**
 * Pagination metadata for the credit cards table.
 */
export interface CreditCardsTablePagination {
  page: number;
  offset: number;
  totalItems: number;
  totalPages: number;
}

/**
 * Normalized response for the credit cards table.
 */
export interface CreditCardsTableDataResult {
  items: CreditCardsTableItem[];
  pagination: CreditCardsTablePagination;
  lastUpdated?: string;
}

/**
 * Data source contract used by the credit cards table.
 */
export interface CreditCardsTableDataSource {
  /**
   * Lists table rows based on query filters.
   *
   * @param query query options
   */
  list(query: CreditCardsTableQuery): Observable<CreditCardsTableDataResult>;

  /**
   * Updates contracted product name for a relationship.
   *
   * @param payload update payload
   */
  updateContractedProduct(payload: CreditCardsUpdateContractedProductPayload): Observable<unknown>;
}

/**
 * Update payload for contracted product.
 */
export interface CreditCardsUpdateContractedProductPayload {
  id: string;
  name: string;
}

/**
 * Optional mapping callback to convert backend payloads to normalized table data.
 */
export type CreditCardsTableResponseMapper = (
  response: unknown,
  query: CreditCardsTableQuery,
) => CreditCardsTableDataResult;

/**
 * Optional request builder callback for the table query.
 */
export type CreditCardsTableRequestBuilder = (query: CreditCardsTableQuery) => unknown;

/**
 * Optional request builder callback for contracted product update.
 */
export type CreditCardsUpdateRequestBuilder = (
  payload: CreditCardsUpdateContractedProductPayload,
) => unknown;

/**
 * HTTP adapter configuration for the credit cards table data source.
 */
export interface CreditCardsTableDataSourceConfig {
  baseUrl: string;
  endpoint: string;
  updateEndpoint?: string;
  method: 'GET' | 'POST';
  buildRequest?: CreditCardsTableRequestBuilder;
  buildUpdateRequest?: CreditCardsUpdateRequestBuilder;
  mapResponse?: CreditCardsTableResponseMapper;
}

/**
 * Parses an AmountDto-shaped object `{ amount: string, currency: string }` to a number.
 */
function parseAmountField(value: unknown): number | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const { amount } = value as Record<string, unknown>;

  if (typeof amount !== 'string') {
    return null;
  }

  const parsed = parseFloat(amount);

  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Maps the credit card account list API response to the normalized table format.
 *
 * Handles the `GET /credit-cards/accounts/list` response shape:
 * `{ count, page, next_page, results: CreditCardAccountDto[] }`
 */
function mapCreditCardAccountListResponse(
  response: unknown,
  query: CreditCardsTableQuery,
): CreditCardsTableDataResult {
  const emptyResult: CreditCardsTableDataResult = {
    items: [],
    pagination: { page: query.page, offset: query.offset, totalItems: 0, totalPages: 0 },
  };

  if (!response || typeof response !== 'object') {
    return emptyResult;
  }

  const data = response as Record<string, unknown>;
  const results = Array.isArray(data['results']) ? (data['results'] as Record<string, unknown>[]) : [];
  const count = typeof data['count'] === 'number' ? data['count'] : results.length;
  const page = typeof data['page'] === 'number' ? data['page'] : query.page;
  const totalPages = Math.max(1, Math.ceil(count / Math.max(1, query.offset)));

  const items: CreditCardsTableItem[] = results.map(
    (account: Record<string, unknown>): CreditCardsTableItem => {
      const nickname =
        typeof account['nickname'] === 'string' && account['nickname'].trim()
          ? account['nickname'].trim()
          : null;
      const name =
        typeof account['name'] === 'string' && account['name'].trim()
          ? account['name'].trim()
          : null;

      return {
        id: typeof account['pk_core'] === 'string' ? account['pk_core'] : undefined,
        bank: typeof account['brand_name'] === 'string' ? account['brand_name'] : '-',
        bankCode:
          typeof account['bank_code'] === 'string' && account['bank_code'].trim()
            ? account['bank_code'].trim()
            : undefined,
        institutionCnpj:
          typeof account['company_cnpj'] === 'string' && account['company_cnpj'].trim()
            ? account['company_cnpj'].trim()
            : undefined,
        brand: typeof account['credit_card_network'] === 'string' ? account['credit_card_network'] : '-',
        name: name ?? undefined,
        contractedProduct: nickname ?? name ?? '-',
        totalLimit: parseAmountField(account['total_limit']),
        usedLimit: parseAmountField(account['used_limit']),
        availableLimit: parseAmountField(account['available_limit']),
        isLimitFlexible: account['is_limit_flexible'] === true,
        dueDay: typeof account['due_day'] === 'number' ? account['due_day'] : undefined,
      };
    },
  );

  return {
    items,
    pagination: { page, offset: query.offset, totalItems: count, totalPages },
    lastUpdated: typeof data['last_updated'] === 'string' && data['last_updated'].trim()
      ? data['last_updated'].trim()
      : undefined,
  };
}

/**
 * Credit cards table data source config token.
 *
 * This token can be overridden at app-level to change source endpoint, request format,
 * and response mapping without changing the component implementation.
 */
export const CREDIT_CARDS_TABLE_DATA_SOURCE_CONFIG =
  new InjectionToken<CreditCardsTableDataSourceConfig>('CREDIT_CARDS_TABLE_DATA_SOURCE_CONFIG', {
    providedIn: 'root',
    factory: (): CreditCardsTableDataSourceConfig => ({
      // Credit cards go through the REST BFF (api-client), which proxies the
      // credit-card gRPC service internally. Do NOT point this at the raw gRPC
      // host (api-credit-card.*): the browser cannot speak gRPC.
      baseUrl: environment.apiUrl,
      endpoint: '/v2/credit-cards/accounts/list',
      updateEndpoint: '/v2/credit-cards/accounts/nickname',
      method: 'GET',
      buildRequest: ({
        cnpj,
        page,
        offset,
        search,
      }: CreditCardsTableQuery): Record<string, unknown> => ({
        client_cnpj: cnpj,
        page,
        page_size: offset,
        // Field name must match the BFF/gRPC contract (ListCreditCardAccountsRequest.search_term).
        // The BFF runs ValidationPipe({ whitelist: true }), so a mismatched param (e.g. `search`)
        // is silently dropped and the list comes back unfiltered. See card 86e1vgq59.
        ...(search?.trim() ? { search_term: search.trim() } : {}),
      }),
      buildUpdateRequest: ({
        id,
        name,
      }: CreditCardsUpdateContractedProductPayload): Record<string, unknown> => ({
        credit_card_account_id: id,
        new_nickname: name,
        correlation_id: crypto.randomUUID(),
      }),
      mapResponse: (response: unknown, query: CreditCardsTableQuery): CreditCardsTableDataResult =>
        mapCreditCardAccountListResponse(response, query),
    }),
  });

/**
 * Credit cards table data source token.
 */
export const CREDIT_CARDS_TABLE_DATA_SOURCE =
  new InjectionToken<CreditCardsTableDataSource>('CREDIT_CARDS_TABLE_DATA_SOURCE');
